import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { hasValue, valueOrDefault } from 'Lib/Any';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../../Data/ReduxViewModels/WorksheetEditor';
import { Button, ButtonGroup, ConfirmDialog, Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { NamePrefixModifier, withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from '../../../../Layout';
import { NuclearDensityGaugeMode } from '../../types';
import { BsNfdReading } from './NuclearDensityReading';

const MAXIMUM_ALLOWED_READINGS = 3;

const Section: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, prefixName }) => {
  const dispatch = useDispatch();

  const confirmGaugeModeChange = useSelector(
    combine(
      [
        createWorksheetSelector!('Measurements.NuclearDensityGaugeMode'),
        createWorksheetSelector!('Measurements.Depth'),
      ],
      (gaugeMode, depth) => gaugeMode === NuclearDensityGaugeMode.DirectTransmission && hasValue(depth)
    )
  );

  const numberOfReadings = useSelector(
    pipe(createWorksheetSelector!('Measurements.NumberOfReadings'), valueOrDefault(1))
  );

  const addReading = () => {
    dispatch(
      actions.setValue({ name: prefixName!('Measurements.NumberOfReadings'), value: numberOfReadings + 1, sync: true })
    );
  };

  const removeReading = () => {
    dispatch(
      actions.setValue({ name: prefixName!('Measurements.NumberOfReadings'), value: numberOfReadings - 1, sync: true })
    );
  };

  const allowDepthInput = useSelector(createWorksheetSelector!('Measurements.AllowEnterProbeDepth')) as boolean;

  return (
    <PageSection heading="Measurements">
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          label="Gauge Mode"
          name="Measurements.NuclearDensityGaugeMode"
          options={[
            { label: 'Direct Transmission', value: NuclearDensityGaugeMode.DirectTransmission },
            { label: 'Backscatter', value: NuclearDensityGaugeMode.Backscatter },
          ]}
          simpleValue
          confirm={confirmGaugeModeChange}
          confirmText={'This action will delete the entered Probe Depth. Are you sure you want to continue?'}
        />
        <Field
          input={allowDepthInput ? <NumericInput /> : <TextOutput />}
          name="Measurements.Depth"
          blankReplacement={allowDepthInput ? undefined : 'N/A'}
        />
      </ListFieldSection>
      {Array(numberOfReadings)
        .fill(undefined)
        ?.map((_, i) => (
          <NamePrefixModifier key={`Measurements.Readings[${i}]`} appendNamePrefix={`Measurements.Readings[${i}]`}>
            <BsNfdReading readingNo={i + 1} heading={i > 0 ? `Reading ${i + 1}` : ''} />
          </NamePrefixModifier>
        ))}

      <ButtonGroup>
        {numberOfReadings > 1 && (
          <ConfirmDialog
            confirm
            confirmText="All data entered for the additional reading will be lost. Delete the additional reading?"
            confirmButtonText="Remove Reading"
            confirmTitle="Remove Reading"
          >
            <Button error onClick={removeReading}>
              Remove Reading
            </Button>
          </ConfirmDialog>
        )}

        {numberOfReadings < MAXIMUM_ALLOWED_READINGS && <Button onClick={addReading}>Add Reading</Button>}
      </ButtonGroup>

      {numberOfReadings > 1 && (
        <NamePrefixModifier appendNamePrefix="Measurements.AverageReadings">
          <ListFieldSection heading="Averages">
            <Field input={<TextOutput />} label="Corrected Bulk Density" name="MeanWetDensity" />
            <Field input={<TextOutput />} label="Corrected Moisture Density" name="MeanMoistureDensity" />
            <Field input={<TextOutput />} label="Corrected Gauge Moisture Content (%)" name="MeanMoistureContent" />
            <Field input={<TextOutput />} label="Gauge Dry Density" name="MeanDryDensity" />
          </ListFieldSection>
        </NamePrefixModifier>
      )}
    </PageSection>
  );
};

const BsMeasurementSection = withInjectedProps(Section);

export { BsMeasurementSection };
export default BsMeasurementSection;
